import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FocusLoggerService implements OnDestroy {
  private static instance: FocusLoggerService | null = null;
  private focusSubscriptions: Subscription[] = [];
  private activeElement: Element | null = null;
  private checkInterval: any;

  constructor(private ngZone: NgZone) {
    FocusLoggerService.instance = this;
  }

  /**
   * Static method to start logging without injection
   */
  static start(options?: FocusLogOptions): void {
    if (!this.instance) {
      throw new Error('FocusLoggerService must be provided in your application root');
    }
    this.instance.startLogging(options);
  }

  /**
   * Static method to stop logging without injection
   */
  static stop(): void {
    if (this.instance) {
      this.instance.stopLogging();
    }
  }

  startLogging(options: FocusLogOptions = { logToConsole: true }) {
    this.stopLogging();

    this.ngZone.runOutsideAngular(() => {
      const focusInSub = fromEvent(document, 'focusin').subscribe((event: Event) => {
        this.handleFocusChange(event, true, options);
      });

      const focusOutSub = fromEvent(document, 'focusout').subscribe((event: Event) => {
        this.handleFocusChange(event, false, options);
      });

      this.focusSubscriptions.push(focusInSub, focusOutSub);

      this.checkInterval = setInterval(() => {
        const currentElement = document.activeElement;
        if (currentElement !== this.activeElement) {
          const fakeEvent = { target: currentElement } as unknown as Event;
          this.handleFocusChange(fakeEvent, true, options);
          this.activeElement = currentElement;
        }
      }, 100);
    });
  }

  stopLogging() {
    this.focusSubscriptions.forEach((sub) => sub.unsubscribe());
    this.focusSubscriptions = [];

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  private handleFocusChange(event: Event, isFocusIn: boolean, options: FocusLogOptions) {
    const target = event.target as HTMLElement;
    const logMessage = this.createLogMessage(target, isFocusIn);
    const logData = this.createLogData(target, isFocusIn);

    this.ngZone.run(() => {
      // Default console logging
      if (options.logToConsole) {
        const consoleMethod = options.consoleMethod || 'log';
        console[consoleMethod](logMessage, target);
      }

      // Custom logger
      if (options.customLogger) {
        options.customLogger(logMessage, logData);
      }

      // UI Logger component
      if (options.uiLoggerComponent) {
        options.uiLoggerComponent.addLogEntry(logData);
      }

      // Local storage logging
      if (options.logToLocalStorage) {
        this.logToLocalStorage(logData);
      }
    });
  }

  private createLogMessage(element: HTMLElement, isFocusIn: boolean): string {
    const tagName = element.tagName.toLowerCase();
    const elementId = element.id ? `#${element.id}` : '';
    const elementClasses = element.className ? `.${element.className.split(' ').join('.')}` : '';
    return `${isFocusIn ? 'Focus in' : 'Focus out'} on ${tagName}${elementId}${elementClasses}`;
  }

  private createLogData(element: HTMLElement, isFocusIn: boolean): FocusLogData {
    return {
      timestamp: new Date(),
      type: isFocusIn ? 'focusin' : 'focusout',
      element: {
        tag: element.tagName.toLowerCase(),
        id: element.id,
        classes: element.className.split(' ').filter((c) => c.trim() !== ''),
        attributes: this.getElementAttributes(element),
      },
      elementRef: element,
    };
  }

  private getElementAttributes(element: HTMLElement): Record<string, string> {
    return Array.from(element.attributes).reduce(
      (acc, attr) => {
        acc[attr.name] = attr.value;
        return acc;
      },
      {} as Record<string, string>,
    );
  }

  private logToLocalStorage(data: FocusLogData): void {
    try {
      const logs = JSON.parse(localStorage.getItem('focusLogs') || '[]');
      logs.push(data);
      localStorage.setItem('focusLogs', JSON.stringify(logs.slice(-100))); // Keep last 100 entries
    } catch (e) {
      console.error('Failed to save focus log to localStorage', e);
    }
  }

  ngOnDestroy() {
    this.stopLogging();
    FocusLoggerService.instance = null;
  }
}

export interface FocusLogOptions {
  /** Log to console (default: true) */
  logToConsole?: boolean;
  /** Console method to use (log, debug, info, warn, error) */
  consoleMethod?: 'log' | 'debug' | 'info' | 'warn' | 'error';
  /** Custom logger function */
  customLogger?: (message: string, data: FocusLogData) => void;
  /** Log to local storage */
  logToLocalStorage?: boolean;
  /** UI Logger component reference */
  uiLoggerComponent?: {
    addLogEntry: (data: FocusLogData) => void;
  };
}

export interface FocusLogData {
  timestamp: Date;
  type: 'focusin' | 'focusout';
  element: {
    tag: string;
    id: string;
    classes: string[];
    attributes: Record<string, string>;
  };
  elementRef: HTMLElement;
}
